import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSession, useLogout } from "../lib/session.js";
import { InstallButton } from "./InstallApp.js";

/**
 * Seven items, down from nine. Standings and Schedule are two views of one
 * competition and the Board is a second view of the same franchise table, so
 * each pair is one destination with tabs rather than two nav entries competing
 * for the same glance.
 */
const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/season", label: "Season" },
  { to: "/leaders", label: "Stats" },
  { to: "/teams", label: "Teams" },
  { to: "/managers", label: "Managers" },
  { to: "/news", label: "News" },
  { to: "/rules", label: "Rules" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { user, isCommissioner } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-2 text-sm font-medium transition ${
      isActive
        ? "bg-field-800 text-chalk-50"
        : "text-chalk-400 hover:bg-field-850 hover:text-chalk-200"
    }`;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-field-800 bg-field-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="text-xl" aria-hidden>
              🏈
            </span>
            <span className="text-base font-bold tracking-tight">
              Madden Pro League
            </span>
          </Link>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {/* Every signed-in control hides below `sm` and moves into the menu.
                The title plus two nav links plus the account chip overflows a
                390px header, and an overflowing header scrolls the whole page
                sideways on every page at once. */}
            {user ? (
              <div className="hidden items-center gap-2 sm:flex">
                <NavLink to="/submit" className={linkClass}>
                  Enter result
                </NavLink>
                {isCommissioner && (
                  <NavLink to="/admin" className={linkClass}>
                    Admin
                  </NavLink>
                )}
                <InstallButton />
                <Link
                  to="/account"
                  className="rounded-lg border border-field-700 px-3 py-1.5 text-sm text-chalk-200 hover:bg-field-850"
                >
                  {user.name}
                </Link>
                <button
                  className="btn-ghost px-2 py-1.5"
                  onClick={() =>
                    logout.mutate(undefined, { onSuccess: () => navigate("/") })
                  }
                >
                  Sign out
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                state={{ from: location.pathname }}
                className="btn-secondary py-1.5"
              >
                Sign in
              </Link>
            )}

            <button
              className="btn-ghost px-2 py-1.5 lg:hidden"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              ☰
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="border-t border-field-800 px-4 pb-3 lg:hidden">
            <div className="flex flex-wrap gap-1 pt-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={linkClass}
                  onClick={() => setMenuOpen(false)}
                >
                  {n.label}
                </NavLink>
              ))}
              {/* The signed-in controls live only here below `sm`, so this is
                  the one route to your account on a phone — which is where the
                  "change your password" prompt sends you. */}
              {user && (
                <>
                  <NavLink
                    to="/submit"
                    className={linkClass}
                    onClick={() => setMenuOpen(false)}
                  >
                    Enter result
                  </NavLink>
                  {isCommissioner && (
                    <NavLink
                      to="/admin"
                      className={linkClass}
                      onClick={() => setMenuOpen(false)}
                    >
                      Admin
                    </NavLink>
                  )}
                  <NavLink
                    to="/account"
                    className={linkClass}
                    onClick={() => setMenuOpen(false)}
                  >
                    {user.name}
                  </NavLink>
                  <InstallButton />
                  <button
                    className="btn-ghost"
                    onClick={() => {
                      setMenuOpen(false);
                      logout.mutate(undefined, { onSuccess: () => navigate("/") });
                    }}
                  >
                    Sign out
                  </button>
                </>
              )}
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">{children}</main>

      <footer className="border-t border-field-800 py-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 text-xs text-chalk-500">
          <p>
            Madden Pro League · Co-Commissioners Ian &amp; Carter
          </p>
          <p>
            <Link to="/rules" className="link">
              Rules
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
